import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Gift, Check, ChevronRight, Sparkles } from 'lucide-react';
import { Card } from './ui/card';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import OptimizedImage from './OptimizedImage';
import { toast } from '../utils/toastPolicy';
import { trackEvent } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-user "we already told you the welcome coffee rolled into the bigger
// reward" flag. The merge note is worth saying ONCE — after that it's just a
// reminder of something the customer no longer has.
const LAPSE_SEEN_KEY = (userId) => `pbc_welcome_lapse_seen_${userId}`;

/** Branch id from localStorage — reward items resolve per-branch (BR #35). */
function branchId() {
  try {
    return JSON.parse(localStorage.getItem('selectedBranch') || 'null')?.id || '';
  } catch {
    return '';
  }
}

/**
 * Welcome Coffee reward card (Business Rule #45) — the in-app half of the
 * app-install campaign. Renders the one-time "your first coffee is on us"
 * reward that a new install earns, in three states:
 *
 *   granted  — pick one of the campaign drinks (the claim action)
 *   claimed  — already picked, waiting in the cart / next order
 *   lapsed   — a one-time note that it merged into the 5-coffee reward
 *
 * Picking a drink routes through ProductDetail exactly like the loyalty
 * reward does (`?free_coffee=true&source=welcome`) so size restrictions,
 * customisation pricing and the claim→cart handoff all reuse one code path.
 *
 * Self-fetching by default so it can be dropped onto Landing without threading
 * state; pass `welcome` when the parent already holds
 * `/free-coffee/progress` (the Offers page) to avoid a duplicate request.
 *
 * @param {object}  [props.welcome]  pre-fetched `welcome` block from /free-coffee/progress
 * @param {boolean} [props.compact]  one-line strip for Home instead of the full card
 */
export default function WelcomeCoffeeCard({ welcome: welcomeProp, compact = false }) {
  const { user, token } = useAuth();
  const { hasFreeCoffeeInCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [welcome, setWelcome] = useState(welcomeProp || null);
  const impressionRef = useRef(false);

  const controlled = welcomeProp !== undefined;

  useEffect(() => {
    if (controlled) setWelcome(welcomeProp || null);
  }, [controlled, welcomeProp]);

  useEffect(() => {
    if (controlled || !user || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const bid = branchId();
        const { data } = await axios.get(
          `${API}/free-coffee/progress${bid ? `?branch_id=${bid}` : ''}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setWelcome(data?.welcome || null);
      } catch { /* silent — the card simply doesn't render */ }
    })();
    return () => { cancelled = true; };
  }, [controlled, user, token]);

  const status = welcome?.status;
  const isLapseNote = status === 'lapsed' && welcome?.lapse_reason === 'loyalty_reward_earned';

  // Suppress the merge note once it has been seen. Read during render is fine —
  // the key only ever flips in the effect below, after the note has shown once.
  const lapseAlreadySeen = (() => {
    if (!isLapseNote || !user?.id) return false;
    try { return !!localStorage.getItem(LAPSE_SEEN_KEY(user.id)); } catch { return false; }
  })();

  const visible = !!welcome && (welcome.active || (isLapseNote && !lapseAlreadySeen));

  useEffect(() => {
    if (!visible || impressionRef.current) return;
    impressionRef.current = true;
    if (isLapseNote) {
      try { localStorage.setItem(LAPSE_SEEN_KEY(user.id), '1'); } catch { /* private mode */ }
      return;
    }
    trackEvent('welcome_coffee_card_shown', { metadata: { status, compact } });
  }, [visible, isLapseNote, status, compact, user]);

  /**
   * Pick a drink → ProductDetail, where the claim is minted at add-to-cart
   * time. `source=welcome` rides in the QUERY STRING (not router state) so a
   * cold deep-link or a hard refresh still claims against the right programme.
   */
  const pick = useCallback((item) => {
    if (hasFreeCoffeeInCart()) {
      toast.error('Free coffee already in cart');
      return;
    }
    trackEvent('welcome_coffee_claim_click', { metadata: { item_name: item.name } });
    navigate(`/menu/item/${item.id}?free_coffee=true&source=welcome`, {
      state: { backgroundLocation: location, rewardSizes: item.free_coffee_sizes || null },
    });
  }, [hasFreeCoffeeInCart, navigate, location]);

  /**
   * Release the reserved drink so the picker returns (claimed → granted).
   * Mirrors the loyalty cancel-claim semantics: changing your mind never
   * consumes the entitlement. Controlled parents (Offers page) refetch via the
   * page's own progress fetch on next focus; the self-fetching variant
   * refetches inline.
   */
  const changeDrink = useCallback(async () => {
    if (hasFreeCoffeeInCart()) {
      toast.error('Remove the free coffee from your cart first');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/free-coffee/cancel-claim`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const bid = branchId();
      const { data } = await axios.get(
        `${API}/free-coffee/progress${bid ? `?branch_id=${bid}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setWelcome(data?.welcome || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not release the drink — try again');
    }
  }, [hasFreeCoffeeInCart]);

  if (!visible) return null;

  const copy = welcome.copy || {};
  const floor = Number(welcome.min_order_value) || 0;

  // ── merge note ───────────────────────────────────────────────────────────
  if (isLapseNote) {
    return (
      <Card className="p-3.5 flex items-start gap-3 bg-muted border-border" data-testid="welcome-coffee-lapsed">
        <Sparkles className="w-[18px] h-[18px] text-[hsl(var(--accent))] shrink-0 mt-0.5" strokeWidth={1.5} />
        <p className="text-[13.5px] leading-relaxed text-muted-foreground">
          Your welcome coffee has rolled into your{' '}
          <strong className="text-foreground font-semibold">bigger free-coffee reward</strong> — same
          free cup, a much wider choice of drinks.
        </p>
      </Card>
    );
  }

  // ── already picked ───────────────────────────────────────────────────────
  // Reserved ≠ in the cart: the customer may have abandoned or cleared the
  // cart after picking, and the claim (server-side) survives that. This state
  // must stay ACTIONABLE — a status line with no button strands the reward
  // (found by the owner in staging). "Add to cart" re-runs the same
  // ProductDetail claim path (idempotent for the same drink); "choose a
  // different drink" releases the claim so the picker returns.
  if (status === 'claimed') {
    const inCart = hasFreeCoffeeInCart();
    // On Home (compact) the claimed state is a tappable strip into Offers,
    // where the actions live — same pattern as the granted strip.
    if (compact) {
      return (
        <button
          type="button"
          onClick={() => navigate('/offer-of-the-day')}
          className="w-full text-left"
          data-testid="welcome-coffee-claimed-strip"
        >
          <Card className="p-3.5 flex items-center gap-3 border-[hsl(var(--accent))]/35 bg-[hsl(var(--accent))]/8 active:scale-[0.99] transition-transform">
            <Check className="w-[18px] h-[18px] text-[hsl(var(--accent))] shrink-0" strokeWidth={2.2} />
            <p className="flex-1 min-w-0 text-[13.5px] leading-snug truncate">
              Free <strong className="font-semibold">{welcome.item_name}</strong> reserved
              {inCart ? " — it's in your cart" : ' — tap to add it to your order'}
            </p>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={2} />
          </Card>
        </button>
      );
    }
    return (
      <Card
        className="p-3.5 bg-[hsl(var(--accent))]/8 border-[hsl(var(--accent))]/30"
        data-testid="welcome-coffee-claimed"
      >
        <div className="flex items-center gap-3">
          <Check className="w-[18px] h-[18px] text-[hsl(var(--accent))] shrink-0" strokeWidth={2.2} />
          <p className="text-[13.5px] leading-snug">
            Your free <strong className="font-semibold">{welcome.item_name}</strong>
            {welcome.size ? ` (${welcome.size})` : ''} is reserved
            {inCart ? " — it's in your cart, place the order to enjoy it." : '.'}
          </p>
        </div>
        {!inCart && (
          <div className="flex items-center gap-2 mt-3 pl-[30px]">
            <button
              type="button"
              onClick={() => {
                trackEvent('welcome_coffee_claim_click', {
                  metadata: { item_name: welcome.item_name, surface: 'claimed_readd' },
                });
                navigate(`/menu/item/${welcome.item_id}?free_coffee=true&source=welcome`, {
                  state: { backgroundLocation: location },
                });
              }}
              className="px-4 py-2 rounded-full text-[13px] font-bold bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] active:scale-95 transition-transform"
              data-testid="welcome-coffee-readd"
            >
              Add to cart
            </button>
            <button
              type="button"
              onClick={changeDrink}
              className="px-3 py-2 text-[13px] font-semibold text-muted-foreground"
              data-testid="welcome-coffee-change-drink"
            >
              Choose a different drink
            </button>
          </div>
        )}
      </Card>
    );
  }

  // ── compact Home strip ───────────────────────────────────────────────────
  if (compact) {
    return (
      <button
        type="button"
        onClick={() => navigate('/offer-of-the-day')}
        className="w-full text-left"
        data-testid="welcome-coffee-strip"
      >
        <Card className="p-3.5 flex items-center gap-3 border-[hsl(var(--accent))]/35 bg-[hsl(var(--accent))]/8 active:scale-[0.99] transition-transform">
          <div className="w-9 h-9 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center shrink-0">
            <Gift className="w-[18px] h-[18px] text-[hsl(var(--accent-foreground))]" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-fraunces font-bold text-[15px] leading-tight tracking-tight">
              {copy.offers_card_title || 'Your welcome coffee'}
            </p>
            <p className="text-[12px] text-muted-foreground leading-tight mt-0.5 truncate">
              Pick your free drink — yours until you use it
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={2} />
        </Card>
      </button>
    );
  }

  // ── full picker ──────────────────────────────────────────────────────────
  const items = welcome.reward_items || [];
  return (
    <Card
      className="overflow-hidden border-[hsl(var(--accent))]/35"
      data-testid="welcome-coffee-card"
    >
      <div className="px-4 pt-4 pb-3 bg-[hsl(var(--accent))]/8 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center shrink-0">
          <Gift className="w-5 h-5 text-[hsl(var(--accent-foreground))]" strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <h3 className="font-fraunces text-[17px] font-bold tracking-tight leading-tight">
            {copy.offers_card_title || 'Your welcome coffee'}
          </h3>
          <p className="text-[12.5px] text-muted-foreground leading-snug mt-0.5">
            {copy.offers_card_sub || 'Thanks for installing the app — pick one, on the house.'}
          </p>
        </div>
      </div>

      {items.length > 0 ? (
        <div className="p-3 space-y-2">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => pick(item)}
              className="w-full flex items-center gap-3 p-2 rounded-lg border border-border hover:border-[hsl(var(--accent))]/60 transition-colors text-left"
              data-testid={`welcome-reward-${item.id}`}
            >
              <OptimizedImage
                src={item.image_urls?.thumb}
                imageUrls={item.image_urls}
                size="thumb"
                alt={item.name}
                width={48}
                height={48}
                eager
                className="w-12 h-12 rounded-md shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold line-clamp-2">{item.name}</p>
                <p className="text-xs text-muted-foreground line-through">₹{item.price}</p>
              </div>
              <span className="text-[11px] font-bold text-[hsl(var(--accent-foreground))] bg-[hsl(var(--accent))] px-2.5 py-1 rounded-full shrink-0">
                FREE
              </span>
            </button>
          ))}
          <p className="text-[11.5px] text-muted-foreground px-1 pt-1 leading-relaxed">
            Yours until you use it — one per account.
            {floor > 0 ? ` Valid on orders above ₹${floor.toFixed(0)}.` : ''}
          </p>
        </div>
      ) : (
        // Every campaign drink is sold out or not carried at this branch.
        <p className="px-4 py-4 text-[13px] text-muted-foreground">
          Your welcome coffee is waiting — none of the offer drinks are available at this store right
          now, so try another branch or check back shortly.
        </p>
      )}
    </Card>
  );
}
